import type { UserRole } from '@/types/user';

/**
 * Role -> capability rules.
 *
 * Mirrors the control plane RBAC matrix (control-plane/internal/router/router.go):
 * - admin: everything
 * - operator: read everything except users/audit; manage domains, origin groups,
 *   certificates (except delete), nginx, tasks; node health checks and Plesk
 *   tests/refresh. Node create/update/delete/token rotation are admin-only.
 * - readonly: read only (no users/audit)
 * - HTTP statistics (stats:read) are visible to every role
 *
 * UI code must ask `can(role, action)` instead of comparing roles inline, so a
 * matrix change only touches this file.
 */
export const ACTIONS = [
  'dashboard:read',

  'nodes:read',
  'nodes:create',
  'nodes:update',
  'nodes:delete',
  'nodes:health',
  'nodes:test-plesk',
  'nodes:refresh-plesk',
  'nodes:rotate-token',

  'domains:read',
  'domains:create',
  'domains:update',
  'domains:delete',
  'domains:validate-dns',
  'domains:setup-node',
  'domains:import-certificate',

  'origin-groups:read',
  'origin-groups:create',
  'origin-groups:update',
  'origin-groups:delete',
  'origin-groups:test',

  'certificates:read',
  'certificates:issue',
  'certificates:import',
  'certificates:renew',
  'certificates:retry',
  'certificates:sync',
  'certificates:monitor',
  'certificates:delete',

  'nginx:read',
  'nginx:generate',
  'nginx:reload',
  'nginx:activate',
  'nginx:regenerate',
  'nginx:delete',

  'tasks:read',
  'tasks:cancel',
  'tasks:retry',

  'stats:read',

  'users:manage',
  'audit:read',
] as const;

export type Action = (typeof ACTIONS)[number];

const READ_ACTIONS: readonly Action[] = [
  'dashboard:read',
  'nodes:read',
  'domains:read',
  'origin-groups:read',
  'certificates:read',
  'nginx:read',
  'tasks:read',
  'stats:read',
];

const OPERATOR_ACTIONS: readonly Action[] = [
  ...READ_ACTIONS,
  'nodes:health',
  'nodes:test-plesk',
  'nodes:refresh-plesk',
  'domains:create',
  'domains:update',
  'domains:delete',
  'domains:validate-dns',
  'domains:setup-node',
  'domains:import-certificate',
  'origin-groups:create',
  'origin-groups:update',
  'origin-groups:delete',
  'origin-groups:test',
  'certificates:issue',
  'certificates:import',
  'certificates:renew',
  'certificates:retry',
  'certificates:sync',
  'certificates:monitor',
  'nginx:generate',
  'nginx:reload',
  'nginx:activate',
  'nginx:regenerate',
  'nginx:delete',
  'tasks:cancel',
  'tasks:retry',
];

/**
 * The permission matrix
 */
const ROLE_PERMISSIONS: Record<UserRole, ReadonlySet<Action>> = {
  admin: new Set<Action>(ACTIONS),
  operator: new Set<Action>(OPERATOR_ACTIONS),
  readonly: new Set<Action>(READ_ACTIONS),
};

/**
 * Whether a user with `role` may perform `action`.
 * Unknown or missing roles are denied everything.
 */
export function can(role: UserRole | string | null | undefined, action: Action): boolean {
  if (!role || !Object.prototype.hasOwnProperty.call(ROLE_PERMISSIONS, role)) return false;
  return ROLE_PERMISSIONS[role as UserRole].has(action);
}
